import React, { useState } from "react";
import {
  User,
  BookOpen,
  LifeBuoy,
  ShoppingBag,
  Settings,
  FileText,
  Menu,
  X,
  LogOut,
  Shield,
} from "lucide-react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { useLogoutUserMutation } from "@/features/api/authApi";
import { toast } from "sonner";

const ProfileSidebar = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [logoutUser] = useLogoutUserMutation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const allowedRoles = ["user", "student", "customer", "instructor", "admin"];
  const role = user?.role;

  if (!isAuthenticated || !allowedRoles.includes(role)) {
    return <Navigate to="/login" replace />;
  }

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md transition
     ${isActive ? "bg-gray-200 font-semibold" : "hover:bg-gray-100"}`;

  const logoutHandler = async () => {
    try {
      await logoutUser().unwrap();
      toast.success("Logged out successfully");
      window.location.href = "/";
    } catch {
      toast.error("Logout failed");
    }
  };

  return (
    <>
      <Header />

      {/* Mobile Header */}
      <div className="lg:hidden flex justify-between items-center px-4 py-3 bg-white border-b">
        <button onClick={() => setMobileOpen(true)}>
          <Menu size={22} />
        </button>
        <h2 className="font-semibold">My Dashboard</h2>
      </div>

      <div className="flex min-h-screen bg-gray-100">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static z-40 top-0 left-0 h-full w-[260px] bg-white border-r p-5
          transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} 
          lg:translate-x-0 transition-transform`}
        >
          {/* IMPORTANT: flex column */}
          <div className="flex flex-col h-full">
            {/* Mobile Close */}
            <div className="lg:hidden flex justify-end mb-4">
              <button onClick={() => setMobileOpen(false)}>
                <X />
              </button>
            </div>

            <h1 className="text-xl font-bold mb-6 border-b pb-3">
              My Account
            </h1>

            {/* Navigation */}
            <nav className="space-y-1">
              <NavLink to="/dashboard/profile" className={navLinkClass}>
                <User size={20} /> Profile
              </NavLink>

              <NavLink to="/dashboard/mycourses" className={navLinkClass}>
                <BookOpen size={20} /> My Courses
              </NavLink>

              <NavLink to="/dashboard/myorders" className={navLinkClass}>
                <ShoppingBag size={20} /> My Orders
              </NavLink>

              {/* <NavLink to="/dashboard/certificates" className={navLinkClass}>
                <FileText size={20} /> Certificates
              </NavLink> */}

              <NavLink to="/online-payment" className={navLinkClass}>
                <LifeBuoy size={20} /> Online Payments
              </NavLink>

               <NavLink to="/dashboard/support" className={navLinkClass}>
               <FileText size={20} /> Support
               </NavLink>   

              {/* <NavLink to="/dashboard/settings" className={navLinkClass}>
                <Settings size={20} /> Settings
              </NavLink> */}

              {role === "instructor" && (
                <NavLink to="/admin/dashboard" className={navLinkClass}>
                  <Shield size={20} /> Instructor Panel
                </NavLink>
              )}

              {role === "admin" && (
                <NavLink to="/admin/dashboard" className={navLinkClass}>
                  <Shield size={20} /> Admin Panel
                </NavLink>
              )}
            </nav>

            {/* Logout – pushed to bottom */}
            <div className="mt-auto pt-4 border-t">
              <button
                onClick={logoutHandler}
                className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-red-600 hover:bg-red-50"
              >
                <LogOut size={20} /> Logout
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 mt-2">
          <Outlet />
        </main>
      </div>

      <Footer />
    </>
  );
};

export default ProfileSidebar;
